import { useMutation, useQuery } from '@apollo/client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { WindowedCombobox } from '@optools/components/combobox/windowed-combobox';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { Form, FormField, FormMessage } from '@optools/components/ui/form';
import { UPSERT_MEDIA_POLICIES } from '@optools/services/operator-gateway/esni/mutations';
import {
  GET_MEDIA_POLICIES,
  GET_VIEWING_POLICIES,
} from '@optools/services/operator-gateway/esni/queries';
import {
  MediaPoliciesQuery,
  MediaPolicy,
  MediaPolicyInput,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

const schema = z.object({
  policyHref: z.string().min(1, { error: 'Please select a viewing policy' }),
});

type AddPolicyFormValues = z.infer<typeof schema>;

export interface AddPolicyDialogProps {
  mediaPolicy: MediaPolicy;
}

export function AddPolicyDialog({ mediaPolicy }: AddPolicyDialogProps) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery(GET_VIEWING_POLICIES, { skip: !open });
  const { id: mediaId } = mediaPolicy;

  const policies = data?.viewingPolicies.map(({ href }) => ({ label: href, value: href })) ?? [];

  const form = useForm<AddPolicyFormValues>({
    defaultValues: { policyHref: '' },
    resolver: zodResolver(schema),
  });

  const [upsertMediaPolicies, { loading }] = useMutation(UPSERT_MEDIA_POLICIES, {
    onCompleted: ({ upsertMediaPolicies }) => {
      const title = 'Viewing Policy Attached';

      if (!upsertMediaPolicies) {
        toast.error(title, {
          description: `Could not attach viewing policy to media ${mediaId}`,
          duration: Infinity,
        });
        return;
      }

      const upsertPolicy = upsertMediaPolicies.find((item) => item?.id === mediaId);

      if (!upsertPolicy) {
        toast.warning(title, {
          description: `Modified policy is not present in response for media ${mediaId}`,
        });
        return;
      }

      toast.success(title, {
        description: `Policy ${form.getValues().policyHref} attached to media ${upsertPolicy.id}`,
      });
      setOpen(false);
      form.reset();
    },
    update: (cache, { data }) => {
      if (!data?.upsertMediaPolicies) return;

      const upsertPolicy = data.upsertMediaPolicies.find((item) => item?.id === mediaId);
      const query = cache.readQuery<MediaPoliciesQuery>({ query: GET_MEDIA_POLICIES });

      if (!query) return;

      const updatedPolicies = query.mediaPolicies.map((policy) => {
        return policy.id === mediaId && upsertPolicy ? upsertPolicy : policy;
      });

      cache.writeQuery<MediaPoliciesQuery>({
        data: { mediaPolicies: updatedPolicies },
        query: GET_MEDIA_POLICIES,
      });
    },
  });

  useEffect(() => {
    if (!open) form.reset();
  }, [open, form]);

  const onSubmit = (values: AddPolicyFormValues) => {
    const { contentDate, feedSubType, feedType, gamePk, id, language, mediaType, requiredAuth } =
      mediaPolicy;

    const viewingPolicyHrefs = mediaPolicy.viewingPolicies
      .map((item) => item.href)
      .concat(values.policyHref);

    const mediaPolicyInput: MediaPolicyInput = {
      contentDate,
      feedSubType,
      feedType,
      gamePk,
      id,
      language,
      mediaType,
      requiredAuth,
      viewingPolicyHrefs,
    };
    upsertMediaPolicies({ variables: { media: mediaPolicyInput } });
  };

  const handleClose = (openValue: boolean) => {
    if (!loading) setOpen(openValue);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <DataTableAddButton>Add Viewing Policy</DataTableAddButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Viewing Policy</DialogTitle>
          <DialogDescription>Search for a viewing policy to attach to this media</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form name="add-policy" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="policyHref"
              render={({ field: { onChange, value } }) => (
                <div className="overflow-hidden">
                  <WindowedCombobox
                    aria-label="viewing policy"
                    data={policies}
                    fitContainer
                    placeHolder="Search viewing policies..."
                    value={value}
                    onValueChange={onChange}
                  />
                  <FormMessage className="mt-2 ml-1" />
                </div>
              )}
            />

            <DialogFooter className="mt-4">
              <DialogClose asChild>
                <Button type="reset" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button disabled={loading} loading={loading} type="submit">
                Add
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
